import { useState, useEffect, useCallback, useRef } from "react";
import axios from "axios";
import useAxiosPrivate from "./useAxiosPrivate";

const CACHE_TTL_MS = 5 * 60 * 1000; // 5 minutes TTL
const MAX_CACHE_SIZE = 100;
const apiCache = new Map();

function getCachedData(url) {
  if (!apiCache.has(url)) return null;
  const entry = apiCache.get(url);
  if (Date.now() - entry.timestamp > CACHE_TTL_MS) {
    apiCache.delete(url);
    return null;
  }
  return entry.data;
}

function setCachedData(url, data) {
  if (apiCache.size >= MAX_CACHE_SIZE) {
    // Evict the first/oldest entry
    const firstKey = apiCache.keys().next().value;
    if (firstKey) apiCache.delete(firstKey);
  }
  apiCache.set(url, { data, timestamp: Date.now() });
}

const useAPINew = (url, options = {}) => {
  const useCache = options.useCache !== false; // defaults to true
  const initialCached = useCache ? getCachedData(url) : null;

  const [data, setData] = useState(() => (initialCached !== null ? initialCached : []));
  const [loading, setLoading] = useState(() => (initialCached === null));
  const [error, setError] = useState(null);
  const axiosPrivate = useAxiosPrivate();
  const abortControllerRef = useRef(null);

  const fetchData = useCallback(
    async (ignoreCache = false) => {
      if (!url) return;

      if (!ignoreCache && useCache) {
        const cached = getCachedData(url);
        if (cached !== null) {
          setData(cached);
          setLoading(false);
          return;
        }
      }

      // Abort any prior in-flight request for this instance
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
      abortControllerRef.current = new AbortController();

      setLoading(true);
      setError(null);

      try {
        const response = await axiosPrivate.post(url, {}, {
          signal: abortControllerRef.current.signal,
        });

        const resultData = response.data?.Data;
        if (useCache && resultData) {
          setCachedData(url, resultData);
        }
        setData(resultData || []);
      } catch (err) {
        // If request was canceled/aborted by unmount, ignore error safely
        if (axios.isCancel(err) || err?.name === "CanceledError" || err?.code === "ERR_CANCELED") {
          return;
        }
        setError(err);
      } finally {
        setLoading(false);
      }
    },
    [url, useCache, axiosPrivate]
  );

  useEffect(() => {
    let isSubscribed = true;
    if (isSubscribed) {
      fetchData();
    }
    return () => {
      isSubscribed = false;
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
    };
  }, [fetchData]);

  const reload = useCallback(() => {
    if (useCache) {
      apiCache.delete(url);
    }
    fetchData(true);
  }, [url, useCache, fetchData]);

  return { data, loading, error, reload };
};

export default useAPINew;
